// Pure helpers for `avatar init` conflict detection and workspace path
// resolution. Extracted from init.ts so they're unit-testable without
// triggering inquirer prompts or git operations.

import { readdir } from "node:fs/promises";
import { join } from "node:path";
import { pathExists } from "../lib/filesystem-helpers.js";
import { AVATAR_MANAGED_PATHS } from "../lib/project-tree-scaffolder.js";

// True if path doesn't exist OR is an empty directory (ignoring dotfiles and
// Windows Thumbs.db noise). Safe to scaffold into. macOS .DS_Store and
// .localized are already filtered by the dotfile rule.
export async function isEmptyOrMissing(path: string): Promise<boolean> {
  if (!(await pathExists(path))) return true;
  try {
    const entries = await readdir(path);
    const meaningful = entries.filter((e) => !e.startsWith(".") && e !== "Thumbs.db");
    return meaningful.length === 0;
  } catch {
    return false;
  }
}

// Return Avatar-managed top-level paths that already exist in projectRoot.
// Caller decides whether to abort, prompt, or auto-backup.
export async function detectAvatarConflicts(projectRoot: string): Promise<string[]> {
  const found: string[] = [];
  for (const rel of AVATAR_MANAGED_PATHS) {
    if (await pathExists(join(projectRoot, rel))) found.push(rel);
  }
  return found;
}

// Find first numbered alternative path (e.g. "foo-2", "foo-3", ...) under
// `parent` that is empty or missing. Returns null if exhausted.
// maxAttempts defaults to 10; if a user has 9+ workspaces with the same
// base name, something else is wrong.
export async function findAlternativeWorkspaceName(
  parent: string,
  desiredName: string,
  maxAttempts = 10,
): Promise<string | null> {
  for (let i = 2; i < maxAttempts; i++) {
    const candidate = join(parent, `${desiredName}-${i}`);
    if (await isEmptyOrMissing(candidate)) return candidate;
  }
  return null;
}
